import { alignmentWalkAtom } from '@/atoms/alignment.atom';
import { sectionOverlaySettingsAtom, stationClipToBinAtom } from '@/atoms/stationVolumes.atom';
import { createHatchTexture, useBandSource } from '@/components/3D/StationBands';
import { internalToDisplay } from '@/core/alignment';
import { useSectionOverlays, useStationOffsets, useStationSections } from '@/hooks/useStationVolumes';
import { binCentreStation, binIndexForStation } from '@/lib/stationVolumes/aggregate';
import { BAND_OPACITY, BAND_STALE_OPACITY } from '@/lib/stationVolumes/bandGeometry';
import { formatM2, staTag } from '@/lib/stationVolumes/format';
import { nearestStationLine, offsetTickLabel } from '@/lib/stationVolumes/sectionModel';
import { chipAreas, sectionOverlayGeometry } from '@/lib/stationVolumes/sectionOverlay';
import { overlaysMatchRun } from '@/lib/stationVolumes/sectionOverlays';
import { activePairIndices, overlaySeries, runSeries, seriesRowsAt } from '@/lib/stationVolumes/sectionSeries';
import type { SvPalette } from '@/lib/stationVolumes/tokens';
import { Html, Line } from '@react-three/drei';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import * as THREE from 'three';

/** atomFamily needs a key even when no dock is open. */
const NO_LAYER = '__none';
/** Ribbon fill, under the hatch (spec §11.2). */
const RIBBON_OPACITY = 0.35;
/** The bottom surface reads as the reference, so it is drawn back (spec §11.2). */
const BOTTOM_LINE_OPACITY = 0.6;
/**
 * How far everything drops back when the alignment no longer hashes to the one
 * the run measured — the bands' own stale ratio, so the section and the
 * corridor dim together in the same frame (A8/A12, ruling D39).
 */
const STALE_DIM = BAND_STALE_OPACITY / BAND_OPACITY;
/** Above the bands (2–7), below the alignment overlay (999). */
const RIBBON_ORDER = 11;
const BOTTOM_LINE_ORDER = 12;
const TOP_LINE_ORDER = 13;
/**
 * Hatch pitch, in metres: a third of the RIBBON's smaller extent — its column
 * width or its own thickness — clamped to something a GPU can still resolve
 * (ruling D38). Sized from the corridor instead, a 0.5 m column spanned about
 * one texel of the 8 × 8 tile and the "hatch" came out as a flat per-column
 * alpha with no diagonal in it at all.
 */
const HATCH_PITCH_MIN_M = 0.05;
const HATCH_PITCH_MAX_M = 1;
/** Interpolation slots for the areas chip — see `legendParts`. */
const CUT_SLOT = '\u0001';
const FILL_SLOT = '\u0002';

/** Two vertices or more — anything less has no segment to draw. */
const isDrawable = (run: Float32Array) => run.length >= 6;

/** A flat `[x,y,z,…]` run as drei's `<Line>` wants it. */
const runPoints = (run: Float32Array): [number, number, number][] =>
  Array.from({ length: run.length / 3 }, (_, k) => [run[k * 3], run[k * 3 + 1], run[k * 3 + 2]]);

/**
 * The areas chip, split into coloured runs so it is its own legend (A7,
 * ruling D40): nothing else in the SCENE says which hue is cut and which is
 * fill, and a client should not have to look back at the dock to find out.
 *
 * The existing `section.areas` string is interpolated with two sentinels and
 * then cut at them, so each figure keeps the words that introduce it —
 * `Cut 12.30` in the cut hue, `/ Fill 4.50` in the fill hue, the trailing
 * `m²` neutral. No new key, and it works whatever order a locale puts the two
 * in.
 */
function legendParts(
  template: string,
  cutText: string,
  fillText: string,
  palette: SvPalette,
): { text: string; color?: string }[] {
  const out: { text: string; color?: string }[] = [];
  let buffer = '';
  for (const token of template.split(/([\u0001\u0002])/)) {
    if (token === CUT_SLOT) {
      out.push({ text: buffer + cutText, color: palette.cut });
      buffer = '';
    } else if (token === FILL_SLOT) {
      out.push({ text: buffer + fillText, color: palette.fill });
      buffer = '';
    } else {
      buffer += token;
    }
  }
  if (buffer) out.push({ text: buffer });
  return out;
}

/**
 * The walked station's cross-section, drawn where it belongs: on the corridor
 * (spec §11.2). The active as-built pair as two polylines at real elevations
 * with a cut/fill ribbon between them, plus every other level, design plane
 * and overlay reference as a plain line — the same SERIES the Section tab
 * draws, off the same rows: the true station-line cut when the run has one,
 * the bin means otherwise (section-overlays spec §2.2). Column for column,
 * because both place columns with `offsetCentre` (ruling D22a).
 *
 * Mounted only while the dock is open on a layer, the Section tab is active
 * or "Clip to station" is on, a byproduct is already decoded, and a walk is
 * standing inside the analysed corridor. It never STARTS either fetch — the
 * Section tab owns both (`enabled: false` below) — so turning the clip on
 * does not silently pull tens of megabytes.
 *
 * The dock → layer → metadata → bins → alignment chain comes from
 * `useBandSource`, the bands' own hook (ruling D39): the two drawings must
 * agree about which run is on screen and, above all, about whether the
 * centreline under them is still the one the numbers were measured on.
 */
export const StationSectionOverlay = () => {
  const { t } = useTranslation('project');
  const { dock, json, meta, overlaysMeta, batchId, alignment, alignmentMatches, palette } = useBandSource();
  const clipToBin = useAtomValue(stationClipToBinAtom(dock?.layerId ?? NO_LAYER));
  const walk = useAtomValue(alignmentWalkAtom);
  // Cached planes only, never a fetch of our own (`enabled: false`) — the
  // Section tab owns all three fetches.
  const { data: offsets } = useStationOffsets(meta, batchId, false);
  const { data: sections } = useStationSections(meta, batchId, false);
  const { data: overlays } = useSectionOverlays(overlaysMeta, batchId, false);
  /**
   * The overlay settings the DOCK resolved for the layer on screen (spec
   * §5.3). Refused when it names a different layer: the dock and this
   * component are mounted independently, so on every dock switch there is a
   * frame where the published list still belongs to the run that was open a
   * moment ago — and drawing a stranger's reference lines on this corridor is
   * exactly the silent lie the id checks below exist to prevent.
   */
  const published = useAtomValue(sectionOverlaySettingsAtom);
  const overlaySettings = dock && published?.layerId === dock.layerId ? published.settings : null;

  const wanted = !!dock && (dock.tab === 'section' || clipToBin);

  // Only while the walk cursor is actually inside the analysed corridor:
  // `binIndexForStation` clamps, so without the range test a cursor 2 km past
  // the last bin would draw the last bin's section as if the user were
  // standing on it (the same test the bands make).
  const walkedBin =
    json && walk && meta && walk.key === meta.alignment.key && json.bins.length
      ? walk.station >= json.params.stationFromInternalM && walk.station <= json.params.stationToInternalM
        ? binIndexForStation(json.params, json.bins.length, walk.station)
        : -1
      : -1;
  const bin = walkedBin >= 0 ? json?.bins[walkedBin] : undefined;

  const origin = useMemo<[number, number, number]>(
    () => (alignment ? [alignment.points[0], alignment.points[1], alignment.points[2]] : [0, 0, 0]),
    [alignment],
  );

  // The same source the 2D section draws (2026-09-01 spec §2.2, finally):
  // the true station-line cut when the run has one, the bin mean otherwise.
  const stationLines = json?.stations ?? null;
  const trueSections = !!stationLines && stationLines.length > 0 && !!sections;
  const lineIndex = trueSections && walk ? nearestStationLine(stationLines, walk.station) : 0;
  const drawnLine = trueSections ? stationLines[lineIndex] : null;
  const drawnStation = drawnLine ? drawnLine.staInternalM : bin ? binCentreStation(bin) : 0;

  /**
   * The run's own grid, and whether the overlays file was sampled ON it — the
   * same acceptance rule the 2D chart applies (`overlaysMatchRun`, I7). A file
   * from a run that has since been re-analysed at a different resolution or
   * swath decodes cleanly and draws every reference at the wrong offset, which
   * in the SCENE is worse than on the chart: the line sits on the terrain and
   * looks surveyed.
   *
   * Deliberately NOT gated on the settings hash: a plane whose id is in the
   * file is that plane, whatever other overlays have been configured since
   * (spec §7.4). `overlaySeries` does the per-id match itself.
   */
  const runGrid = useMemo(
    () =>
      trueSections && sections
        ? { trueSections: true, nStationLines: sections.nStationLines, nOffsets: sections.nOffsets }
        : offsets
          ? { trueSections: false, nStationLines: offsets.nStations, nOffsets: offsets.nOffsets }
          : null,
    [trueSections, sections, offsets],
  );
  const overlaysUsable = !!overlays && !!runGrid && overlaysMatchRun(overlays.meta, runGrid);

  const series = useMemo(
    () => [
      ...runSeries(trueSections ? sections : null, trueSections ? null : offsets),
      ...(overlaysUsable && overlaySettings ? overlaySeries(overlays, overlaySettings, palette.series) : []),
    ],
    [trueSections, sections, offsets, overlaysUsable, overlays, overlaySettings, palette.series],
  );
  const rows = useMemo(
    () => seriesRowsAt(series, trueSections ? lineIndex : walkedBin),
    [series, trueSections, lineIndex, walkedBin],
  );
  const axisSource = trueSections ? sections : offsets;

  const geometry = useMemo(() => {
    if (!wanted || !axisSource || !alignment || !bin || !meta || rows.asbuilt.length < 2) return null;
    // Phase 1: the ribbon is drawn between the FIRST two as-built levels (the
    // dock's chip selection does not reach this component yet); every other
    // level and every design line is drawn as a plain line.
    const [ia, ib] = activePairIndices(rows, null);
    const extra = [
      ...rows.asbuilt.flatMap((z, i) =>
        i === ia || i === ib ? [] : [{ key: rows.asbuiltIds[i], role: 'asbuilt' as const, z }],
      ),
      ...rows.design.map((z, i) => ({ key: `design-${i}`, role: 'design' as const, z })),
      // The overlay's own ink, the same index-in-the-full-list colour the 2D
      // legend and the popover's swatch use: a reference is drawn coloured
      // precisely because it came from somewhere else.
      ...rows.reference.map((r) => ({
        key: r.series.id,
        role: 'reference' as const,
        color: r.series.color,
        z: r.row,
      })),
    ];
    return sectionOverlayGeometry({
      zTop: rows.asbuilt[ia],
      zBottom: rows.asbuilt[ib],
      extra,
      axis: {
        offsetStartM: axisSource.offsetStartM,
        offsetStepM: axisSource.offsetStepM,
        nOffsets: axisSource.nOffsets,
      },
      alignment,
      station: drawnStation,
      origin,
      swathLeftM: meta.params.swathLeftM,
      swathRightM: meta.params.swathRightM,
      cut: palette.cut,
      fill: palette.fill,
    });
  }, [wanted, axisSource, alignment, bin, meta, rows, drawnStation, origin, palette.cut, palette.fill]);

  const ribbonGeom = useMemo(() => {
    if (!geometry?.ribbon) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(geometry.ribbon.positions, 3));
    g.setAttribute('color', new THREE.BufferAttribute(geometry.ribbon.colors, 3));
    g.setAttribute('uv', new THREE.BufferAttribute(geometry.ribbon.uv, 2));
    g.setIndex(new THREE.BufferAttribute(geometry.ribbon.index, 1));
    g.computeBoundingSphere();
    return g;
  }, [geometry]);
  useEffect(() => () => ribbonGeom?.dispose(), [ribbonGeom]);

  // The bands' own 45° alpha tile, not a second copy of that canvas (ruling
  // D20a). The ribbon's UVs are `(offset, elevation)` in metres and are
  // mirrored on cut columns, so cut and fill come out on opposite diagonals
  // and the ribbon still reads in greyscale or on a projector.
  const hatch = useMemo(() => createHatchTexture(), []);
  useEffect(() => () => hatch.dispose(), [hatch]);

  // Isotropic, and sized to the RIBBON (D38) so the diagonal actually
  // resolves: the smaller of the column width and the ribbon's own thickness,
  // divided by three, clamped. Low coverage is NOT signalled here any more —
  // a change of stripe pitch is not decodable without a reference — it gets a
  // categorical cue on the areas chip instead.
  const columnWidthM = axisSource?.offsetStepM ?? 0;
  const ribbonThicknessM = geometry?.ribbon
    ? Math.max(Math.abs(geometry.ribbon.dzMinM), Math.abs(geometry.ribbon.dzMaxM))
    : 0;
  useEffect(() => {
    const extents = [columnWidthM, ribbonThicknessM].filter((v) => v > 0);
    const pitch = extents.length
      ? Math.min(HATCH_PITCH_MAX_M, Math.max(HATCH_PITCH_MIN_M, Math.min(...extents) / 3))
      : HATCH_PITCH_MAX_M;
    // `matrixAutoUpdate` is on, so the renderer folds this into the map's uv
    // transform on the next frame — no `updateMatrix()` of our own.
    hatch.repeat.set(1 / pitch, 1 / pitch);
  }, [hatch, columnWidthM, ribbonThicknessM]);

  // A run of ONE measured column is a point, not a line: drei's `Line` would
  // build a Line2 with no segment in it. The ribbon still shows that column,
  // so nothing measured is lost.
  const topLines = useMemo(() => geometry?.topRuns.filter(isDrawable).map(runPoints) ?? [], [geometry]);
  const bottomLines = useMemo(() => geometry?.bottomRuns.filter(isDrawable).map(runPoints) ?? [], [geometry]);

  if (!geometry || !bin || !alignment || !meta) return null;

  // Data honesty (A8/A12, ruling D39): on a stale alignment the bands go dim
  // and amber, and the section — the more precise-looking drawing of the two
  // — must not be presented at full confidence beside them.
  const dim = alignmentMatches ? 1 : STALE_DIM;
  const chipBorder = alignmentMatches ? palette.zero : palette.warn;

  // The label is the station actually DRAWN — the true station line on a run
  // that has one, the bin's CENTRE otherwise — in display stationing (D23),
  // never the bin's start.
  const stationLabel = staTag(internalToDisplay(alignment, drawnStation));
  // Same figures, same formatter as the Section tab's own readout (A15), and
  // the same SUBJECT: on a true-section run the chip prints the drawn station
  // LINE's own end areas, because that is what the drawing beside it shows;
  // only on a run with no station lines does it print the bin's interval
  // means. An unmeasured figure is an em dash, never a zero (`chipAreas`).
  const chip = chipAreas(drawnLine, bin);
  const areas = legendParts(
    t('stationVolumes.section.areas', { cut: CUT_SLOT, fill: FILL_SLOT }),
    chip.cutM2 == null ? '—' : formatM2(chip.cutM2),
    chip.fillM2 == null ? '—' : formatM2(chip.fillM2),
    palette,
  );
  // `15L` / `15R` — the section's own offset convention (`offsetTickLabel`),
  // so the swath ends in the scene are labelled the way the 2D chart labels
  // that very offset on its axis. A zero-width side gets NO chip: the swath
  // has no end there, and `offsetTickLabel(0)` would print `CL` on top of the
  // station chip.
  const leftLabel = meta.params.swathLeftM > 0 ? offsetTickLabel(-meta.params.swathLeftM) : null;
  const rightLabel = meta.params.swathRightM > 0 ? offsetTickLabel(meta.params.swathRightM) : null;

  // The labels are drei `Html` with no `distanceFactor`, so they are screen
  // sized by construction — no `camera.position.distanceTo` anywhere in this
  // file (CLAUDE.md's unitsPerPixel rule).
  const chipStyle: CSSProperties = {
    whiteSpace: 'nowrap',
    padding: '2px 5px',
    borderRadius: 4,
    // The scene is not the dock, so `var(--sv-*)` is unavailable here; the
    // overlay takes the same palette object the bands do, which follows the
    // app's light/dark theme.
    background: `color-mix(in srgb, ${palette.surface} 92%, transparent)`,
    border: `1px solid ${chipBorder}`,
    color: palette.ink,
    fontSize: 11,
    lineHeight: 1.35,
    fontVariantNumeric: 'tabular-nums',
    opacity: dim,
  };
  // On the `Html` WRAPPER, not only on the chip inside it: drei renders its
  // own absolutely-positioned div around `children`, and that div keeps
  // `pointer-events: auto` unless a style says otherwise — four chips sitting
  // on the walked station were eating orbit drags and wheel zooms
  // (`StationBands.tsx` passes exactly this).
  const htmlStyle: CSSProperties = { pointerEvents: 'none', userSelect: 'none' };

  return (
    <group position={origin}>
      {ribbonGeom && (
        <mesh geometry={ribbonGeom} renderOrder={RIBBON_ORDER} raycast={() => null}>
          {/* `depthTest: false` for the same reason the bands use it: the
              ribbon is annotation, and the surfaces it describes are drawn at
              the very elevations it occupies, so a depth-tested ribbon comes
              out half-buried in them. */}
          <meshBasicMaterial
            map={hatch}
            vertexColors
            transparent
            opacity={RIBBON_OPACITY * dim}
            side={THREE.DoubleSide}
            depthTest={false}
            depthWrite={false}
          />
        </mesh>
      )}
      {/* The two surfaces take the SECTION's surface ink (`palette.haul`, the
          `--sv-surface-ink` token the 2D chart draws its surfaces with —
          ruling D21a), not the hover ink: a surface line is a geometry mark,
          not a cut/fill category. */}
      {bottomLines.map((points, i) => (
        <Line
          key={`b${i}`}
          points={points}
          color={palette.haul}
          lineWidth={2}
          transparent
          opacity={BOTTOM_LINE_OPACITY * dim}
          depthWrite={false}
          depthTest={false}
          renderOrder={BOTTOM_LINE_ORDER}
          raycast={() => null}
        />
      ))}
      {/* Every OTHER series: the rest of a stack's as-built levels, the design
          planes (dashed — a design is not a measurement), and the overlay
          references. Same ink as the pair, distinguished by weight and
          opacity, so nothing here reads as a cut/fill claim. */}
      {geometry.extraRuns.map((line) =>
        line.runs.filter(isDrawable).map((run, i) => (
          <Line
            key={`x-${line.key}-${i}`}
            points={runPoints(run)}
            color={line.color ?? palette.haul}
            lineWidth={line.role === 'reference' ? 1.5 : 1.25}
            dashed={line.role === 'design'}
            dashSize={0.4}
            gapSize={0.3}
            transparent
            opacity={(line.role === 'design' ? 0.55 : line.role === 'reference' ? 0.9 : 0.5) * dim}
            depthWrite={false}
            depthTest={false}
            renderOrder={BOTTOM_LINE_ORDER}
            raycast={() => null}
          />
        )),
      )}
      {topLines.map((points, i) => (
        <Line
          key={`t${i}`}
          points={points}
          color={palette.haul}
          lineWidth={2}
          transparent
          opacity={dim}
          depthWrite={false}
          depthTest={false}
          renderOrder={TOP_LINE_ORDER}
          raycast={() => null}
        />
      ))}
      <Html position={geometry.anchors.station} center zIndexRange={[10, 0]} style={htmlStyle}>
        <div
          data-testid='sv-section-3d-station'
          style={{ ...chipStyle, transform: 'translateY(-140%)', fontFamily: 'ui-monospace, Menlo, monospace' }}>
          {stationLabel}
        </div>
      </Html>
      {leftLabel && (
        <Html position={geometry.anchors.left} center zIndexRange={[10, 0]} style={htmlStyle}>
          <div style={chipStyle}>{leftLabel}</div>
        </Html>
      )}
      {rightLabel && (
        <Html position={geometry.anchors.right} center zIndexRange={[10, 0]} style={htmlStyle}>
          <div style={chipStyle}>{rightLabel}</div>
        </Html>
      )}
      <Html position={geometry.anchors.areas} center zIndexRange={[10, 0]} style={htmlStyle}>
        <div data-testid='sv-section-3d-areas' style={{ ...chipStyle, transform: 'translateY(140%)' }}>
          {areas.map((part, i) => (
            <span key={i} style={part.color ? { color: part.color } : undefined}>
              {part.text}
            </span>
          ))}
          {/* Low coverage is a CATEGORY, not a texture the reader has to
              decode: the bands' own words, in the bands' own warn colour
              (ruling D38). */}
          {bin.lowCoverage && <span style={{ color: palette.warn }}> · {t('stationVolumes.bands.lowCoverage')}</span>}
        </div>
      </Html>
    </group>
  );
};
